import React, { createContext, useContext } from "react";
import cx from "classnames";
import { node, string, bool, oneOfType, arrayOf, oneOf } from "prop-types";
import styles from "./CheckboxGroup.module.scss";
import { useCheckboxGroup } from "./useCheckboxGroup";

const CheckboxGroupContext = createContext();

const useCheckboxGroupContext = () => useContext(CheckboxGroupContext);

/**
 * A component that can contain multiple checkboxes and passes
 * values down to each child checkbox using the Context API.
 * `Checkbox` components can consume these values using the
 * `useCheckboxGroupContext` hook.
 */

const CheckboxGroup = (props) => {
  const { children, className } = props;
  const classNames = cx(
    styles.checkboxGroup,
    {
      [styles.column]: props.direction === "column",
      [styles.row]: props.direction === "row",
      [styles.spacingSm]: props.spacing === "sm",
      [styles.spacingMd]: props.spacing === "md",
      [styles.spacingLg]: props.spacing === "lg",
    },
    className
  );
  const groupValues = useCheckboxGroup(props);

  return (
    <div className={classNames}>
      <CheckboxGroupContext.Provider value={groupValues}>
        {children}
      </CheckboxGroupContext.Provider>
    </div>
  );
};

export default CheckboxGroup;

CheckboxGroup.propTypes = {
  /** The children checkboxes to return */
  children: node.isRequired,
  /** For CSS customisation */
  className: string,
  /** Initial values for checkbox group */
  defaultValue: oneOfType([string, arrayOf(string)]),
  /** The direction of the group, column or row */
  direction: oneOf(["column", "row"]),
  /** If true, all wrapped checkboxes will be disabled */
  isDisabled: bool,
  /** The spacing between each checkbox */
  spacing: oneOf(["sm", "md", "lg"]),
};

CheckboxGroup.defaultProps = {
  direction: "row",
  spacing: "md",
};

export { useCheckboxGroupContext };
